import { defaultSchema } from "hast-util-sanitize"

import type { Schema } from "hast-util-sanitize"

// Exact classes used by answer-parser.tsx's extraction-citation button/badge
// markup (both are app-generated, trusted, deterministic HTML — not derived
// from LLM output — so an exact allowlist is appropriate here rather than a
// broad pattern). Keep this in sync with that markup; drift is caught by
// markdown-sanitize-schema.test.tsx's extraction-citation case.
const EXTRACTION_CITATION_BUTTON_CLASSES = ["supContainer", "inline-flex", "align-baseline"]
const EXTRACTION_CITATION_BADGE_CLASSES = [
  "inline-flex",
  "items-center",
  "rounded",
  "border",
  "border-primary",
  "bg-primary/15",
  "px-1.5",
  "py-px",
  "text-[10px]",
  "font-medium",
  "text-foreground",
  "transition-colors",
  "hover:bg-primary/25",
]

/**
 * Sanitize schema for chat markdown rendered via rehypeRaw + rehypeKatex.
 * Extends hast-util-sanitize's defaultSchema with the specific allowances
 * this app's rendering pipeline depends on:
 * - `code`'s `math-inline`/`math-display` classes: rehype-katex's own
 *   documented pairing with rehype-sanitize (KaTeX's own generated markup
 *   is produced after this runs, so it never needs to be allowlisted here).
 * - `span`'s `streaming-*` classes: the streaming-cursor indicator injected
 *   directly into activeTail in system-message.tsx. Its other allowed
 *   classes are the extraction-citation badge (see above).
 * - `button`: the extraction-citation control (answer-parser.tsx's
 *   isExtractionCitation branch) — not in hast-util-sanitize's defaultSchema
 *   at all, so it needs its own entry, including the data-* attributes
 *   system-message.tsx's click handler reads off it.
 * - `cite:`/`artifact:` href protocols: citation-link.tsx and
 *   artifact-stream-parser.ts's internal link scheme.
 * - `sandbox:` src protocol: inline-session-image.tsx's session-file paths.
 */
export const chatMarkdownSanitizeSchema: Schema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), "button"],
  attributes: {
    ...defaultSchema.attributes,
    code: [["className", /^language-./, "math-inline", "math-display"]],
    span: [["className", /^streaming-(bar|block|dot|fade)$/, ...EXTRACTION_CITATION_BADGE_CLASSES]],
    button: [
      ["className", ...EXTRACTION_CITATION_BUTTON_CLASSES],
      ["type", "button"],
      "dataCitationRaw",
      "dataMessageIndex",
    ],
  },
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href ?? []), "cite", "artifact"],
    src: [...(defaultSchema.protocols?.src ?? []), "sandbox"],
  },
}

/**
 * Main-answer variant: admit sandbox hrefs only far enough for SystemMessage's
 * custom link renderer to validate them against registered assistant files.
 * Other consumers of the base schema continue stripping sandbox hrefs.
 */
export const chatMessageMarkdownSanitizeSchema: Schema = {
  ...chatMarkdownSanitizeSchema,
  protocols: {
    ...chatMarkdownSanitizeSchema.protocols,
    href: [...(chatMarkdownSanitizeSchema.protocols?.href ?? []), "sandbox"],
  },
}
